import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
  Chip,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Psychology as BrainIcon,
  AccountTree as GitGraphIcon,
  Loop as RepeatIcon,
  Input as FileInputIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Bolt as BoltIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Palette as PaletteIcon,
  Chat as ChatIcon,
  Book as BookIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import InferenceStepBox from '../Boxes/Steps/InferenceStepBox';
import CodeStepBox from '../Boxes/Steps/CodeStepBox';
import ApiStepBox from '../Boxes/Steps/ApiStepBox';

import SemanticMemoryBox from '../Boxes/Memories/SemanticMemoryBox';
import SemanticMemory from '../../models/Memories/SemanticMemory';
import WorkingMemoryBox from '../Boxes/Memories/WorkingMemoryBox';
import EpisodicMemoryBox from '../Boxes/Memories/EpisodicMemoryBox';
import { useEnvironment } from '../../context/EnvironmentContext';
import WorkflowService from '../../services/WorkflowService';
import { AVAILABLE_MODELS, DEFAULT_MODEL, DEFAULT_TEMPERATURE, ModelType } from '../../config/models';
import ProceduralMemory from '../../models/Memories/ProceduralMemory';
import InferenceChainStep from '../../models/Steps/InferenceChainStep';
import CodeStep from '../../models/Steps/CodeStep';
import ApiStep from '../../models/Steps/ApiStep';
import StepModel from '../../models/Steps/Step';
import ProceduralMemoryBox from '../Boxes/Memories/ProceduralMemoryBox';
import PineconeConnection from '../../models/Connections/PineconeConnection';
import AWSConnection from '../../models/Connections/AWSConnection';
import MongoDBConnection from '../../models/Connections/MongoDBConnection';
import AgentNode from '../../models/Nodes/AgentNode';

const getCostColor = (cost: string) => {
  switch (cost) {
    case 'Very High': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'primary';
    case 'Low': return 'success';
    case 'Very Low': return 'info';
    default: return 'default';
  }
};

interface NodeEditWizardModalProps {
  open: boolean;
  onClose: () => void;
  node: any;
  onUpdate: (updatedNode: any) => void;
  allNodes?: any[];
  getEdges: () => any[];
  setEdges: (edges: any[]) => void;
  workflowInputs: Record<string, any>;
  setWorkflowInputs: (inputs: Record<string, any>) => void;
}

// Utility to ensure all connections are class instances
function ensureConnectionInstance(conn: any) {
  if (conn instanceof PineconeConnection || conn instanceof AWSConnection || conn instanceof MongoDBConnection) {
    return conn;
  }
  if (conn.type === 'pinecone') return new PineconeConnection(conn);
  if (conn.type === 'aws') return new AWSConnection(conn);
  if (conn.type === 'mongodb_json') return new MongoDBConnection(conn);
  throw new Error('Unknown connection type');
}

const NodeEditWizardModal: React.FC<NodeEditWizardModalProps> = ({ 
  open, 
  onClose, 
  node, 
  onUpdate,
  allNodes = [],
  getEdges,
  setEdges,
  workflowInputs,
  setWorkflowInputs
}) => {
  const { environment } = useEnvironment();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [nodeType, setNodeType] = useState(node?.data?.type || 'agent');
  const [parameters, setParameters] = useState<string[]>(node?.data?.agent?.requiredParameters || []);
  const [newParameter, setNewParameter] = useState('');
  const [dependencies, setDependencies] = useState<string[]>([]);
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [modelTemperature, setModelTemperature] = useState<number>(DEFAULT_TEMPERATURE);
  const [currentMemoryTab, setCurrentMemoryTab] = useState(0);
  const [currentCognitiveTab, setCurrentCognitiveTab] = useState(0);
  
  // Single source of truth for the node being edited
  const [editingNode, setEditingNode] = useState<any>(null);
  // Add local state for proceduralMemory
  const [localProceduralMemory, setLocalProceduralMemory] = useState<any>(null);
  // Add local state for semantic memory
  const [localSemanticMemory, setLocalSemanticMemory] = useState<SemanticMemory>(new SemanticMemory(0.1, []));

  const currentNodeIndex = allNodes.findIndex(n => n.id === node?.id);
  const isFirstNode = currentNodeIndex === 0;
  const isWorkflowNode = nodeType === 'workflow';
  const isInputNode = nodeType === 'input_node';
  const isLoopNode = nodeType === 'loop';

  // Determine which memory types are available based on node type
  const hasProceduralMemory = !isWorkflowNode;
  const hasSemanticMemory = !isWorkflowNode;
  const hasWorkingMemory = !isWorkflowNode;
  const hasEpisodicMemory = !isWorkflowNode;

  const tabs = [
    {
      title: 'Basic Info',
      description: 'Configure node type and basic settings'
    },
    {
      title: 'Memories',
      description: 'Configure different memory types'
    },
    {
      title: 'Cognitive Abilities',
      description: 'Configure cognitive abilities'
    }
  ];

  const memoryTabs = [
    { title: 'PROCEDURAL MEMORY', icon: <BrainIcon /> },
    { title: 'SEMANTIC MEMORY', icon: <SearchIcon /> },
    { title: 'WORKING MEMORY', icon: <BookIcon /> },
    { title: 'EPISODIC MEMORY', icon: <ChatIcon /> }
  ];

  const cognitiveTabs = [
    { title: 'EXECUTE', icon: <BoltIcon />, description: 'Execute actions and commands' },
    { title: 'LEARN', icon: <BrainIcon />, description: 'Learn from experiences' },
    { title: 'RETRIEVE', icon: <SearchIcon />, description: 'Retrieve information' },
    { title: 'REASON', icon: <AutoAwesomeIcon />, description: 'Reason and analyze' },
    { title: 'DESIGN', icon: <PaletteIcon />, description: 'Design and create' },
    { title: 'REFLECT', icon: <ChatIcon />, description: 'Reflect and evaluate' }
  ];

  // Initialize editing node only when modal opens
  useEffect(() => {
    if (open && node) {
      // Create a deep copy of the node to avoid reference issues
      const nodeCopy = JSON.parse(JSON.stringify(node));
      setEditingNode(nodeCopy);
      
      setNodeType(node.data.type || 'agent');
      if (node.data.agent?.workflowId) {
        setSelectedWorkflowId(node.data.agent.workflowId);
      }
      setParameters(node.data.agent?.requiredParameters || []);
      
      // Load model settings
      if (node.data.agent?.proceduralMemory) {
        setModel(node.data.agent.proceduralMemory.model || DEFAULT_MODEL);
        setModelTemperature(node.data.agent.proceduralMemory.modelTemperature || DEFAULT_TEMPERATURE);
        setLocalProceduralMemory(node.data.agent.proceduralMemory);
      } else {
        const emptyMemory = new ProceduralMemory(DEFAULT_MODEL, DEFAULT_TEMPERATURE, [], undefined, '');
        setLocalProceduralMemory(emptyMemory);
      }

      // Load semantic memory
      if (node.data.agent?.semanticMemory) {
        setLocalSemanticMemory(node.data.agent.semanticMemory);
      } else {
        const emptySemanticMemory = new SemanticMemory(0.1, []);
        setLocalSemanticMemory(emptySemanticMemory);
      }

      const currentEdges = getEdges();
      const nodeDependencies = currentEdges
        .filter(edge => edge.target === node.id)
        .map(edge => edge.source);
      setDependencies(nodeDependencies);
    }
  }, [open, node?.id]); // Only depend on open and node.id

  useEffect(() => {
    if (open && isWorkflowNode) {
      fetchWorkflows();
    }
  }, [open, isWorkflowNode, environment]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.listWorkflows();
      const workflowsData = Array.isArray(response.data?.workflows) ? response.data.workflows : [];
      setWorkflows(workflowsData);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  // Simple update function that only updates the editing node
  const updateEditingNode = useCallback((updates: any) => {
    setEditingNode(prev => {
      if (!prev) return prev;
    
    const updatedNode = {
        ...prev,
      ...updates,
      data: {
          ...prev.data,
        ...updates.data,
        agent: {
            ...prev.data.agent,
            ...updates.data?.agent
        }
      }
    };

      return updatedNode;
    });
  }, []);

  // Update parent only when explicitly requested
  const saveToParent = useCallback(() => {
    if (editingNode) {
      onUpdate(editingNode);
    }
  }, [editingNode, onUpdate]);

  // Step update handler (mutate steps array in place, keep reference stable)
  const handleStepUpdate = (updatedStep: any) => {
    setLocalProceduralMemory((prev: any) => {
      if (!prev) return prev;
      const idx = prev.steps.findIndex((s: any) => s.id === updatedStep.id);
      if (idx === -1) return prev;
      // Mutate the step object in place
      prev.steps[idx] = updatedStep;
      // Return the same object reference to keep array and object identity stable
      return prev;
    });
  };

  // Immediate update function for blur events (same as above)
  const handleStepUpdateImmediate = handleStepUpdate;

  // Step add handler
  const addStep = (type: 'inference_chain' | 'code' | 'api', cognitiveAbility?: string) => {
    let newStep: any;
    switch (type) {
      case 'inference_chain':
        newStep = new InferenceChainStep(
          'New Inference Step',
          {
            model: model as ModelType,
            model_temp: modelTemperature,
            prompt: '',
            variables: {},
            retriever: null,
            is_streaming: false,
            condition: 'True'
          }
        );
        break;
      case 'code':
        newStep = new CodeStep(
          'New Code Step',
          {
            code: '',
            variables: {},
            condition: 'True'
          }
        );
        break;
      case 'api':
        newStep = new ApiStep(
          'New API Step',
          {
            type: 'GET',
            endpoint: '',
            headers: {},
            params: {},
            body: {},
            auth: null,
            timeout: null,
            condition: 'True'
          }
        );
        break;
      default:
        return;
    }

    if (cognitiveAbility && cognitiveAbility !== 'execute') {
      // Add to specific cognitive ability
      setLocalProceduralMemory((prev: any) => ({
        ...prev,
        cognitiveAbilities: {
          ...prev.cognitiveAbilities,
          [cognitiveAbility.toLowerCase()]: {
            ...prev.cognitiveAbilities[cognitiveAbility.toLowerCase()],
            enabled: true,
            steps: [...(prev.cognitiveAbilities[cognitiveAbility.toLowerCase()]?.steps || []), newStep]
          }
        }
      }));
    } else {
      // Add to main procedural memory steps (for execute)
      setLocalProceduralMemory((prev: any) => ({ ...prev, steps: [...prev.steps, newStep] }));
    }
  };

  // Step delete handler
  const removeStep = (id: string, cognitiveAbility?: string) => {
    if (cognitiveAbility && cognitiveAbility !== 'execute') {
      // Remove from specific cognitive ability
      setLocalProceduralMemory((prev: any) => ({
        ...prev,
        cognitiveAbilities: {
          ...prev.cognitiveAbilities,
          [cognitiveAbility.toLowerCase()]: {
            ...prev.cognitiveAbilities[cognitiveAbility.toLowerCase()],
            steps: prev.cognitiveAbilities[cognitiveAbility.toLowerCase()]?.steps?.filter((step: any) => step.id !== id) || []
          }
        }
      }));
    } else {
      // Remove from main procedural memory steps (for execute)
      setLocalProceduralMemory((prev: any) => ({ ...prev, steps: prev.steps.filter((step: any) => step.id !== id) }));
    }
  };

  // Step update handler for cognitive abilities
  const handleCognitiveStepUpdate = (updatedStep: any, cognitiveAbility: string) => {
    setLocalProceduralMemory((prev: any) => {
      if (!prev) return prev;
      const abilityKey = cognitiveAbility.toLowerCase();
      const ability = prev.cognitiveAbilities[abilityKey];
      if (!ability) return prev;
      
      const idx = ability.steps.findIndex((s: any) => s.id === updatedStep.id);
      if (idx === -1) return prev;
      
      // Mutate the step object in place
      ability.steps[idx] = updatedStep;
      return prev;
    });
  };

  // Get steps for a specific cognitive ability
  const getCognitiveSteps = (cognitiveAbility: string) => {
    if (cognitiveAbility === 'execute') {
      // For execute, return steps from main procedural memory
      return localProceduralMemory?.steps || [];
    }
    
    // For other cognitive abilities, return from cognitiveAbilities
    if (!localProceduralMemory?.cognitiveAbilities) return [];
    const abilityKey = cognitiveAbility.toLowerCase();
    return localProceduralMemory.cognitiveAbilities[abilityKey]?.steps || [];
  };

  // Toggle cognitive ability enabled state
  const toggleCognitiveAbility = (cognitiveAbility: string, enabled: boolean) => {
    setLocalProceduralMemory((prev: any) => ({
      ...prev,
      cognitiveAbilities: {
        ...prev.cognitiveAbilities,
        [cognitiveAbility.toLowerCase()]: {
          ...prev.cognitiveAbilities[cognitiveAbility.toLowerCase()],
          enabled: enabled
        }
      }
    }));
  };

  // Get cognitive ability enabled state
  const getCognitiveAbilityEnabled = (cognitiveAbility: string) => {
    if (cognitiveAbility === 'execute') {
      // For execute, always return true since it's the main procedural memory
      return true;
    }
    
    if (!localProceduralMemory?.cognitiveAbilities) return false;
    const abilityKey = cognitiveAbility.toLowerCase();
    return localProceduralMemory.cognitiveAbilities[abilityKey]?.enabled || false;
  };

  // When user clicks Finish, push localProceduralMemory to editingNode and call onUpdate
  const handleFinish = () => {
    setEditingNode((prev: any) => {
      if (!prev) return prev;
      // Create a new ProceduralMemory object only on save
      const updatedProceduralMemory = new ProceduralMemory(
        localProceduralMemory.model,
        localProceduralMemory.modelTemperature,
        [...localProceduralMemory.steps],
        localProceduralMemory.cognitiveAbilities,
        localProceduralMemory.feedback
      );
      const updatedNode = {
        ...prev,
        data: {
          ...prev.data,
          agent: {
            ...prev.data.agent,
            proceduralMemory: updatedProceduralMemory,
            semanticMemory: localSemanticMemory
          }
        }
      };
      
      // Update the actual AgentNode instance if it exists
      if (updatedNode.data.agent && updatedNode.data.agent instanceof AgentNode) {
        updatedNode.data.agent.semanticMemory = localSemanticMemory;
        updatedNode.data.agent.proceduralMemory = updatedProceduralMemory;
      }
      
      onUpdate(updatedNode);
      return updatedNode;
    });
    onClose();
  };

  const nodeTypeOptions = [
    {
      value: 'agent',
      label: 'Agent',
      icon: <BrainIcon fontSize="small" />,
      description: 'Standard agent node for processing tasks'
    },
    {
      value: 'workflow',
      label: 'Workflow',
      icon: <GitGraphIcon fontSize="small" />,
      description: 'Node that executes a sub-workflow'
    },
    {
      value: 'loop',
      label: 'Loop',
      icon: <RepeatIcon fontSize="small" />,
      description: 'Node that iterates over a collection'
    },
    {
      value: 'input_node',
      label: 'Input',
      icon: <FileInputIcon fontSize="small" />,
      description: 'Node for workflow input processing'
    }
  ];

  const handleNodeTypeChange = (value: string) => {
    setNodeType(value);
    updateEditingNode({
      data: {
        type: value,
        agent: {
          type: value,
          childData: value === 'loop' ? editingNode?.data?.agent?.childData : null,
          loopElementsKey: value === 'loop' ? editingNode?.data?.agent?.loopElementsKey : null,
          workflowId: value === 'workflow' ? selectedWorkflowId : null
        }
      }
    });
  };

  const handleWorkflowIdChange = (value: string) => {
    setSelectedWorkflowId(value);
    updateEditingNode({
      data: {
        type: 'workflow',
        agent: {
          type: 'workflow',
          workflowId: value
        }
      }
    });
  };

  const handleModelChange = (event: any) => {
    const newModel = event.target.value as string;
    setModel(newModel);
    updateEditingNode({
      data: {
        agent: {
          proceduralMemory: {
            ...editingNode?.data?.agent?.proceduralMemory,
            model: newModel
          }
        }
      }
    });
  };

  const handleTemperatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value) && value >= 0 && value <= 1) {
      setModelTemperature(value);
      updateEditingNode({
        data: {
          agent: {
            proceduralMemory: {
              ...editingNode?.data?.agent?.proceduralMemory,
              modelTemperature: value
            }
          }
        }
      });
    }
  };

  const addParameter = () => {
    if (newParameter.trim() && !parameters.includes(newParameter.trim())) {
      const updatedParameters = [...parameters, newParameter.trim()];
      setParameters(updatedParameters);
      setNewParameter('');
      updateEditingNode({
        data: {
          agent: {
            requiredParameters: updatedParameters
          }
        }
      });
    }
  };

  const removeParameter = (param: string) => {
    const updatedParameters = parameters.filter(p => p !== param);
    setParameters(updatedParameters);
    updateEditingNode({
      data: {
        agent: {
          requiredParameters: updatedParameters
        }
      }
    });
  };

  const handleDependencyChange = (nodeId: string, checked: boolean) => {
    const updatedDependencies = checked
      ? [...dependencies, nodeId]
      : dependencies.filter(id => id !== nodeId);
    
    setDependencies(updatedDependencies);

    const currentEdges = getEdges();
    const updatedEdges = currentEdges.filter(edge => edge.target !== node.id);

    updatedDependencies.forEach(sourceId => {
      updatedEdges.push({
        id: `e${sourceId}-${node.id}`,
        source: sourceId,
        target: node.id,
        animated: true,
        style: { stroke: 'rgb(206, 0, 179)' }
      });
    });

    setEdges(updatedEdges);

    updateEditingNode({
      data: {
        agent: {
          dependencies: updatedDependencies
        }
      }
    });
  };

  const getAvailableDependencies = () => {
    return allNodes.filter(n => n.id !== node.id && n.data.type !== 'input_node');
  };

  // Simplified procedural memory change handler
  const handleProceduralMemoryChange = useCallback((updatedMemory: any) => {
    setLocalProceduralMemory(updatedMemory);
    updateEditingNode({
        data: {
          agent: {
            proceduralMemory: updatedMemory
          }
        }
    });
  }, [updateEditingNode]);

  const handleSemanticMemoryChange = useCallback((updatedMemory: SemanticMemory) => {
    // Update local semantic memory state
    setLocalSemanticMemory(updatedMemory);
    
    // Directly update the editing node to ensure semantic memory is preserved
    setEditingNode((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          agent: {
            ...prev.data.agent,
            semanticMemory: updatedMemory
          }
        }
      };
    });
  }, []);

  // Execute Ability Steps Section (moved from ProceduralMemory)
  const ExecuteAbilitySteps = () => {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BoltIcon color="primary" />
              <Typography variant="h6">Execute Ability Steps</Typography>
            </Box>
            <FormControlLabel
              control={<Switch />}
              label=""
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Execute actions and commands
          </Typography>
          
          {localProceduralMemory?.steps?.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Box sx={{ width: 40, height: 50, bgcolor: 'green', borderRadius: 1 }} />
                  <Box sx={{ width: 40, height: 50, bgcolor: 'red', borderRadius: 1 }} />
                  <Box sx={{ width: 40, height: 50, bgcolor: 'blue', borderRadius: 1 }} />
                </Stack>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No steps configured yet. Add steps to enable this cognitive ability.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => addStep('inference_chain', 'execute')}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Inference Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('code', 'execute')}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Code Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('api', 'execute')}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add API Step
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => addStep('inference_chain', 'execute')}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Inference Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('code', 'execute')}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Code Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('api', 'execute')}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add API Step
                </Button>
              </Stack>
              
              <DragDropContext onDragEnd={(result) => {
                if (!result.destination) return;
                const items = Array.from(localProceduralMemory.steps) as StepModel[];
                const [reorderedItem] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reorderedItem);
                handleStepUpdateImmediate({ ...reorderedItem, index: result.destination.index });
              }}>
                <Droppable droppableId="steps-droppable">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {localProceduralMemory.steps.map((step: any, index: number) => (
                        <Draggable key={step.id} draggableId={step.id} index={index}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{ mb: 2, boxShadow: snapshot.isDragging ? 4 : 1, borderRadius: 2, bgcolor: 'white' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                                <Box {...provided.dragHandleProps} sx={{ mr: 1, cursor: 'grab' }}>
                                  <DragIndicatorIcon fontSize="small" color="disabled" />
                                </Box>
                                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                  {step.description || step.type}
                                </Typography>
                                <IconButton size="small" color="error" onClick={() => removeStep(step.id, 'execute')}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              <Box sx={{ p: 2 }}>
                                {step.type === 'inference_chain' && (
                                  <InferenceStepBox
                                    stepData={step}
                                    onUpdate={handleStepUpdate}
                                    onUpdateImmediate={handleStepUpdateImmediate}
                                    workflowInputs={workflowInputs}
                                    setWorkflowInputs={setWorkflowInputs}
                                  />
                                )}
                                {step.type === 'code' && (
                                  <CodeStepBox
                                    stepData={step}
                                    onUpdate={handleStepUpdate}
                                    onUpdateImmediate={handleStepUpdateImmediate}
                                    workflowInputs={workflowInputs}
                                  />
                                )}
                                {step.type === 'api' && (
                                  <ApiStepBox
                                    stepData={step}
                                    onUpdate={handleStepUpdate}
                                    onUpdateImmediate={handleStepUpdateImmediate}
                                    workflowInputs={workflowInputs}
                                    setWorkflowInputs={setWorkflowInputs}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Generic Cognitive Ability Steps Component
  const CognitiveAbilitySteps = ({ ability, icon, description }: { ability: string, icon: React.ReactNode, description: string }) => {
    const steps = getCognitiveSteps(ability);
    const isEnabled = getCognitiveAbilityEnabled(ability);

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {icon}
              <Typography variant="h6">{ability} Ability Steps</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch 
                  checked={isEnabled}
                  onChange={(e) => toggleCognitiveAbility(ability, e.target.checked)}
                />
              }
              label=""
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
          
          {steps.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Box sx={{ width: 40, height: 50, bgcolor: 'green', borderRadius: 1 }} />
                  <Box sx={{ width: 40, height: 50, bgcolor: 'red', borderRadius: 1 }} />
                  <Box sx={{ width: 40, height: 50, bgcolor: 'blue', borderRadius: 1 }} />
                </Stack>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No steps configured yet. Add steps to enable this cognitive ability.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => addStep('inference_chain', ability)}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Inference Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('code', ability)}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Code Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('api', ability)}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add API Step
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => addStep('inference_chain', ability)}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Inference Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('code', ability)}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add Code Step
                </Button>
                <Button
                  variant="contained"
                  onClick={() => addStep('api', ability)}
                  sx={{ 
                    bgcolor: '#ce00b3',
                    '&:hover': { bgcolor: '#a8008f' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  + Add API Step
                </Button>
              </Stack>
              
              <DragDropContext onDragEnd={(result) => {
                if (!result.destination) return;
                const items = Array.from(steps) as StepModel[];
                const [reorderedItem] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reorderedItem);
                handleCognitiveStepUpdate({ ...reorderedItem, index: result.destination.index }, ability);
              }}>
                <Droppable droppableId={`${ability}-steps-droppable`}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {steps.map((step: any, index: number) => (
                        <Draggable key={step.id} draggableId={step.id} index={index}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{ mb: 2, boxShadow: snapshot.isDragging ? 4 : 1, borderRadius: 2, bgcolor: 'white' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                                <Box {...provided.dragHandleProps} sx={{ mr: 1, cursor: 'grab' }}>
                                  <DragIndicatorIcon fontSize="small" color="disabled" />
                                </Box>
                                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                  {step.description || step.type}
                                </Typography>
                                <IconButton size="small" color="error" onClick={() => removeStep(step.id, ability)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              <Box sx={{ p: 2 }}>
                                {step.type === 'inference_chain' && (
                                  <InferenceStepBox
                                    stepData={step}
                                    onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, ability)}
                                    onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, ability)}
                                    workflowInputs={workflowInputs}
                                    setWorkflowInputs={setWorkflowInputs}
                                  />
                                )}
                                {step.type === 'code' && (
                                  <CodeStepBox
                                    stepData={step}
                                    onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, ability)}
                                    onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, ability)}
                                    workflowInputs={workflowInputs}
                                  />
                                )}
                                {step.type === 'api' && (
                                  <ApiStepBox
                                    stepData={step}
                                    onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, ability)}
                                    onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, ability)}
                                    workflowInputs={workflowInputs}
                                    setWorkflowInputs={setWorkflowInputs}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderMemoryContent = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Memory Configuration</Typography>
        <Tabs 
          value={currentMemoryTab} 
          onChange={(e, newValue) => setCurrentMemoryTab(newValue)} 
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {memoryTabs.map((tab, index) => (
            <Tab 
              key={tab.title} 
              label={tab.title}
              icon={tab.icon}
              iconPosition="start"
              sx={{ 
                textTransform: 'none',
                fontWeight: currentMemoryTab === index ? 600 : 400,
                minHeight: 48
              }}
            />
          ))}
        </Tabs>
        
        {currentMemoryTab === 0 && hasProceduralMemory && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Procedural Memory Configuration</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Model Configuration</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" gutterBottom>Model</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={model}
                        onChange={handleModelChange}
                        displayEmpty
                      >
                        {AVAILABLE_MODELS.map((modelOption) => (
                          <MenuItem key={modelOption.id} value={modelOption.id}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{modelOption.name}</Typography>
                              <Chip 
                                label={modelOption.cost} 
                                size="small"
                                color={getCostColor(modelOption.cost)}
                              />
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" gutterBottom>Temperature</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleTemperatureChange({ target: { value: Math.max(0, modelTemperature - 0.1).toString() } } as any)}
                        sx={{ 
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          width: 32,
                          height: 32
                        }}
                      >
                        -
                      </IconButton>
                      <TextField
                        value={modelTemperature}
                        onChange={handleTemperatureChange}
                        type="number"
                        inputProps={{ min: 0, max: 1, step: 0.1 }}
                        size="small"
                        sx={{ width: 80 }}
                      />
                      <IconButton 
                        size="small" 
                        onClick={() => handleTemperatureChange({ target: { value: Math.min(1, modelTemperature + 0.1).toString() } } as any)}
                        sx={{ 
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          width: 32,
                          height: 32
                        }}
                      >
                        +
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Creativity level (0.0 - 1.0)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {currentMemoryTab === 1 && hasSemanticMemory && (
          <SemanticMemoryBox
            memory={(() => {
              const mem = localSemanticMemory || new SemanticMemory(0.1, []);
              const safeConnections = mem.connections.map(ensureConnectionInstance);
              return new SemanticMemory(mem.similarityThreshold, safeConnections);
            })()}
            onMemoryChange={handleSemanticMemoryChange}
          />
        )}
        
        {currentMemoryTab === 2 && hasWorkingMemory && (
          <WorkingMemoryBox />
        )}
        
        {currentMemoryTab === 3 && hasEpisodicMemory && (
          <EpisodicMemoryBox />
        )}
      </Box>
    );
  };

  const renderCognitiveAbilitiesContent = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Cognitive Abilities Configuration</Typography>
        <Tabs 
          value={currentCognitiveTab} 
          onChange={(e, newValue) => setCurrentCognitiveTab(newValue)} 
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {cognitiveTabs.map((tab, index) => (
            <Tab 
              key={tab.title} 
              label={tab.title}
              icon={tab.icon}
              iconPosition="start"
              sx={{ 
                textTransform: 'none',
                fontWeight: currentCognitiveTab === index ? 600 : 400,
                minHeight: 48
              }}
            />
          ))}
        </Tabs>
        
        {currentCognitiveTab === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BoltIcon sx={{ color: '#ce00b3', fontSize: 24 }} />
                  <Typography variant="h6">Execute Ability Steps</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getCognitiveAbilityEnabled('execute')}
                      onChange={(e) => toggleCognitiveAbility('execute', e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Execute actions and commands
              </Typography>
              
              {getCognitiveSteps('execute').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    opacity: 0.6
                  }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 2, 
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ddd'
                    }}>
                      <BoltIcon sx={{ color: '#999', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No steps configured yet. Add steps to enable this cognitive ability.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'execute')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'execute')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'execute')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <DragDropContext onDragEnd={handleCognitiveStepUpdate}>
                    <Droppable droppableId="execute-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {getCognitiveSteps('execute').map((step, index) => (
                            <Draggable key={step.id} draggableId={step.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {step.type === 'inference_chain' && (
                                    <InferenceStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'execute')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'execute')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                  {step.type === 'code' && (
                                    <CodeStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'execute')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'execute')}
                                      workflowInputs={{}}
                                    />
                                  )}
                                  {step.type === 'api' && (
                                    <ApiStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'execute')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'execute')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'execute')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'execute')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'execute')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        
        {currentCognitiveTab === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon sx={{ color: '#ce00b3', fontSize: 24 }} />
                  <Typography variant="h6">Learn Ability Steps</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getCognitiveAbilityEnabled('learn')}
                      onChange={(e) => toggleCognitiveAbility('learn', e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Learn from data and experiences
              </Typography>
              
              {getCognitiveSteps('learn').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    opacity: 0.6
                  }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 2, 
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ddd'
                    }}>
                      <SettingsIcon sx={{ color: '#999', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No steps configured yet. Add steps to enable this cognitive ability.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'learn')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'learn')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'learn')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <DragDropContext onDragEnd={handleCognitiveStepUpdate}>
                    <Droppable droppableId="learn-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {getCognitiveSteps('learn').map((step, index) => (
                            <Draggable key={step.id} draggableId={step.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {step.type === 'inference_chain' && (
                                    <InferenceStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'learn')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'learn')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                  {step.type === 'code' && (
                                    <CodeStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'learn')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'learn')}
                                      workflowInputs={{}}
                                    />
                                  )}
                                  {step.type === 'api' && (
                                    <ApiStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'learn')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'learn')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'learn')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'learn')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'learn')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {currentCognitiveTab === 2 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchIcon sx={{ color: '#ce00b3', fontSize: 24 }} />
                  <Typography variant="h6">Retrieve Ability Steps</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getCognitiveAbilityEnabled('retrieve')}
                      onChange={(e) => toggleCognitiveAbility('retrieve', e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Retrieve information from memory
              </Typography>
              
              {getCognitiveSteps('retrieve').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    opacity: 0.6
                  }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 2, 
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ddd'
                    }}>
                      <SearchIcon sx={{ color: '#999', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No steps configured yet. Add steps to enable this cognitive ability.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'retrieve')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'retrieve')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'retrieve')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <DragDropContext onDragEnd={handleCognitiveStepUpdate}>
                    <Droppable droppableId="retrieve-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {getCognitiveSteps('retrieve').map((step, index) => (
                            <Draggable key={step.id} draggableId={step.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {step.type === 'inference_chain' && (
                                    <InferenceStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'retrieve')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'retrieve')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                  {step.type === 'code' && (
                                    <CodeStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'retrieve')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'retrieve')}
                                      workflowInputs={{}}
                                    />
                                  )}
                                  {step.type === 'api' && (
                                    <ApiStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'retrieve')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'retrieve')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'retrieve')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'retrieve')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'retrieve')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {currentCognitiveTab === 3 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon sx={{ color: '#ce00b3', fontSize: 24 }} />
                  <Typography variant="h6">Reason Ability Steps</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getCognitiveAbilityEnabled('reason')}
                      onChange={(e) => toggleCognitiveAbility('reason', e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Reason and analyze information
              </Typography>
              
              {getCognitiveSteps('reason').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    opacity: 0.6
                  }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 2, 
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ddd'
                    }}>
                      <AutoAwesomeIcon sx={{ color: '#999', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No steps configured yet. Add steps to enable this cognitive ability.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'reason')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'reason')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'reason')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <DragDropContext onDragEnd={handleCognitiveStepUpdate}>
                    <Droppable droppableId="reason-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {getCognitiveSteps('reason').map((step, index) => (
                            <Draggable key={step.id} draggableId={step.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {step.type === 'inference_chain' && (
                                    <InferenceStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reason')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reason')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                  {step.type === 'code' && (
                                    <CodeStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reason')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reason')}
                                      workflowInputs={{}}
                                    />
                                  )}
                                  {step.type === 'api' && (
                                    <ApiStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reason')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reason')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'reason')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'reason')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'reason')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {currentCognitiveTab === 4 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon sx={{ color: '#ce00b3', fontSize: 24 }} />
                  <Typography variant="h6">Design Ability Steps</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getCognitiveAbilityEnabled('design')}
                      onChange={(e) => toggleCognitiveAbility('design', e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Design and create solutions
              </Typography>
              
              {getCognitiveSteps('design').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    opacity: 0.6
                  }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 2, 
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ddd'
                    }}>
                      <PaletteIcon sx={{ color: '#999', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No steps configured yet. Add steps to enable this cognitive ability.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'design')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'design')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'design')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <DragDropContext onDragEnd={handleCognitiveStepUpdate}>
                    <Droppable droppableId="design-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {getCognitiveSteps('design').map((step, index) => (
                            <Draggable key={step.id} draggableId={step.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {step.type === 'inference_chain' && (
                                    <InferenceStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'design')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'design')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                  {step.type === 'code' && (
                                    <CodeStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'design')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'design')}
                                      workflowInputs={{}}
                                    />
                                  )}
                                  {step.type === 'api' && (
                                    <ApiStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'design')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'design')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'design')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'design')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'design')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {currentCognitiveTab === 5 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatIcon sx={{ color: '#ce00b3', fontSize: 24 }} />
                  <Typography variant="h6">Reflect Ability Steps</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getCognitiveAbilityEnabled('reflect')}
                      onChange={(e) => toggleCognitiveAbility('reflect', e.target.checked)}
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Reflect and evaluate performance
              </Typography>
              
              {getCognitiveSteps('reflect').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    opacity: 0.6
                  }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 2, 
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ddd'
                    }}>
                      <ChatIcon sx={{ color: '#999', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No steps configured yet. Add steps to enable this cognitive ability.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'reflect')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'reflect')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'reflect')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <DragDropContext onDragEnd={handleCognitiveStepUpdate}>
                    <Droppable droppableId="reflect-steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {getCognitiveSteps('reflect').map((step, index) => (
                            <Draggable key={step.id} draggableId={step.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {step.type === 'inference_chain' && (
                                    <InferenceStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reflect')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reflect')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                  {step.type === 'code' && (
                                    <CodeStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reflect')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reflect')}
                                      workflowInputs={{}}
                                    />
                                  )}
                                  {step.type === 'api' && (
                                    <ApiStepBox
                                      stepData={step}
                                      onUpdate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reflect')}
                                      onUpdateImmediate={(updatedStep) => handleCognitiveStepUpdate(updatedStep, 'reflect')}
                                      setWorkflowInputs={() => {}}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => addStep('inference_chain', 'reflect')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Inference Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('code', 'reflect')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add Code Step
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => addStep('api', 'reflect')}
                      sx={{
                        bgcolor: '#ce00b3',
                        '&:hover': { bgcolor: '#a8008f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1
                      }}
                    >
                      + Add API Step
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 0: // Basic Info
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Required Parameters</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Parameter Name"
                    value={newParameter}
                    onChange={(e) => setNewParameter(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addParameter()}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={addParameter}
                    disabled={!newParameter.trim()}
                    sx={{ 
                      bgcolor: '#ce00b3',
                      '&:hover': { bgcolor: '#a8008f' },
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 1
                    }}
                  >
                    Add
                  </Button>
                </Box>
                
                {parameters.length > 0 && (
                  <Stack spacing={1}>
                  {parameters.map((param) => (
                      <Box key={param} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={param} color="primary" variant="outlined" />
                        <IconButton
                          size="small"
                          onClick={() => removeParameter(param)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                  ))}
                </Stack>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Dependencies</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select nodes that this node depends on:
                </Typography>
                <Box 
                  sx={{ 
                    border: '2px dashed #ccc',
                    borderRadius: 1,
                    p: 3,
                    minHeight: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f9f9f9'
                  }}
                >
                  {dependencies.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No dependencies selected
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      {getAvailableDependencies().map((depNode) => (
                        <Box key={depNode.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={dependencies.includes(depNode.id)}
                            onChange={(e) => handleDependencyChange(depNode.id, e.target.checked)}
                          />
                          <Typography variant="body2">{depNode.data.label}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Stack>
        );

      case 1: // Memories
        return renderMemoryContent();

      case 2: // Cognitive Abilities
        return renderCognitiveAbilitiesContent();

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ 
          width: 32, 
          height: 32, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: '#ce00b3',
          color: 'white'
        }}>
          <BoltIcon />
        </Box>
        <Typography variant="h5" fontWeight="bold">
          Agent Definition
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={tab.title} 
              label={tab.title}
              sx={{ 
                textTransform: 'none',
                fontWeight: currentTab === index ? 600 : 400
              }}
            />
          ))}
        </Tabs>
        
        <Box sx={{ minHeight: 400 }}>
          {renderTabContent()}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            borderColor: '#000',
            color: '#000',
            '&:hover': { 
              borderColor: '#000',
              bgcolor: 'rgba(0,0,0,0.04)'
            },
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1
          }}
        >
          Cancel
        </Button>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Button
          variant="contained"
          onClick={handleFinish}
          sx={{ 
            bgcolor: '#ce00b3',
            '&:hover': { bgcolor: '#a8008f' },
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1
          }}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeEditWizardModal;